import React, { useEffect, useRef, useState } from 'react';
import { Check, Minus, Music, Plus } from 'lucide-react';

const SUCCESS_FEEDBACK_MS = 850;

const useRowPress = () => {
  const [pressed, setPressed] = useState(false);
  return {
    pressed,
    pressProps: {
      onPointerDown: () => setPressed(true),
      onPointerUp: () => setPressed(false),
      onPointerCancel: () => setPressed(false),
      onPointerLeave: () => setPressed(false),
      onKeyDown: event => {
        if (event.key === 'Enter' || event.key === ' ') setPressed(true);
      },
      onKeyUp: () => setPressed(false),
      onBlur: () => setPressed(false)
    }
  };
};

const TrackContent = ({ track, formatDuration, showNumber, index }) => (
  <>
    {showNumber && <span className="track-number">{index + 1}</span>}
    <div className="track-image">
      {track.image ? <img src={track.image} alt={track.name} loading="lazy" /> : <Music size={32} />}
    </div>
    <div className="track-details">
      <h3>{track.name}</h3>
      <p>{track.artist}</p>
    </div>
    <span className="duration">{formatDuration(track.duration)}</span>
  </>
);

export function AddTrackRow({ track, index, onAdd, formatDuration, showNumber = false }) {
  const [status, setStatus] = useState('idle');
  const pendingRef = useRef(false);
  const successTimerRef = useRef(null);
  const { pressed, pressProps } = useRowPress();

  useEffect(() => () => clearTimeout(successTimerRef.current), []);

  const add = async () => {
    if (pendingRef.current || status === 'success') return;
    pendingRef.current = true;
    setStatus('pending');
    try {
      const succeeded = await onAdd(track);
      if (!succeeded) {
        setStatus('idle');
        return;
      }
      setStatus('success');
      successTimerRef.current = setTimeout(() => setStatus('idle'), SUCCESS_FEEDBACK_MS);
    } catch {
      setStatus('idle');
    } finally {
      pendingRef.current = false;
    }
  };

  return (
    <div className={`track-item${pressed ? ' is-action-pressed' : ''}`}>
      <button
        type="button"
        className="track-action-button add-button pressable-control"
        onClick={add}
        disabled={status !== 'idle'}
        aria-label={status === 'success' ? `Added ${track.name}` : `Add ${track.name}`}
        data-state={status}
        {...pressProps}
      >
        {status === 'success' ? <Check size={32} /> : <Plus size={32} />}
      </button>
      <TrackContent track={track} formatDuration={formatDuration} showNumber={showNumber} index={index} />
    </div>
  );
}

export function QueueTrackRow({ track, index, onRemove, formatDuration }) {
  const [pending, setPending] = useState(false);
  const pendingRef = useRef(false);
  const { pressed, pressProps } = useRowPress();

  const remove = async () => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    try {
      const removed = await onRemove(track, index);
      if (!removed) setPending(false);
    } catch {
      setPending(false);
    } finally {
      pendingRef.current = false;
    }
  };

  return (
    <div className={`track-item priority-track${pressed ? ' is-action-pressed' : ''}`}>
      <button
        type="button"
        className="track-action-button remove-button pressable-control"
        onClick={remove}
        disabled={pending}
        aria-label={`Remove ${track.name} from queue`}
        {...pressProps}
      >
        <Minus size={34} />
      </button>
      <TrackContent track={track} formatDuration={formatDuration} index={index} />
    </div>
  );
}
